import { useRef, useState } from 'react';

import {
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  Link as LinkIcon,
  Loader2,
  Lock,
  Minus,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  useAvailableSharedFiles,
  useBackfillRagFileStorage,
  useDeleteRagFile,
  useIngestRagUrl,
  useMakeRagFilePublic,
  useRagFiles,
  useRevokeRagFilePublic,
  useSetRagFileShared,
  useSubscribeRagFile,
  useUnsubscribeRagFile,
  useUploadRagFile,
} from '@/hooks/useRag';
import { extractErrorMessage } from '@/lib/utils';

interface RagKnowledgeSectionProps {
  configurationId?: string;
  ragEnabled: boolean;
  onRagEnabledChange: (value: boolean) => void;
}

const STATE_STYLES: Record<string, string> = {
  ACTIVE: 'bg-emerald-500/10 text-emerald-600',
  PENDING: 'bg-amber-500/10 text-amber-600',
  ERROR: 'bg-red-500/10 text-red-600',
};

export const RagKnowledgeSection = ({
  configurationId,
  ragEnabled,
  onRagEnabledChange,
}: RagKnowledgeSectionProps) => {
  const { toast } = useToast();
  const configId = configurationId ?? '';
  const { data: files = [], isLoading } = useRagFiles(configId);
  const { data: availableShared = [] } = useAvailableSharedFiles(configId);
  const uploadMutation = useUploadRagFile(configId);
  const ingestUrlMutation = useIngestRagUrl(configId);
  const setSharedMutation = useSetRagFileShared(configId);
  const deleteMutation = useDeleteRagFile(configId);
  const makePublicMutation = useMakeRagFilePublic(configId);
  const revokePublicMutation = useRevokeRagFilePublic(configId);
  const subscribeMutation = useSubscribeRagFile(configId);
  const unsubscribeMutation = useUnsubscribeRagFile(configId);
  const backfillMutation = useBackfillRagFileStorage(configId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backfillInputRef = useRef<HTMLInputElement>(null);
  const [backfillTargetId, setBackfillTargetId] = useState<string | null>(null);
  const [url, setUrl] = useState('');

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    try {
      await uploadMutation.mutateAsync(file);
      toast({
        variant: 'success',
        title: 'File added',
        description: `"${file.name}" is indexing.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleAddUrl = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    try {
      await ingestUrlMutation.mutateAsync(trimmed);
      setUrl('');
      toast({ variant: 'success', title: 'URL added', description: 'Page content is indexing.' });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to fetch URL',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleToggleShared = async (id: string, isShared: boolean) => {
    try {
      await setSharedMutation.mutateAsync({ id, isShared });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update sharing',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ variant: 'success', title: 'Removed', description: `"${name}" deleted.` });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleMakePublic = async (id: string) => {
    try {
      const result = await makePublicMutation.mutateAsync({ id });
      await navigator.clipboard.writeText(result.url);
      toast({
        variant: 'success',
        title: 'Public link created',
        description: 'Link copied to clipboard. Anyone with this link can view the document.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to create public link',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleCopyPublicLink = async (token: string) => {
    const publicUrl = `${window.location.origin}/d/${token}`;
    await navigator.clipboard.writeText(publicUrl);
    toast({
      variant: 'success',
      title: 'Copied',
      description: 'Public link copied to clipboard.',
    });
  };

  const handleRevokePublic = async (id: string) => {
    try {
      await revokePublicMutation.mutateAsync(id);
      toast({
        variant: 'success',
        title: 'Public link revoked',
        description: 'The link no longer works.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to revoke link',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleBackfillSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (backfillInputRef.current) backfillInputRef.current.value = '';
    if (!file || !backfillTargetId) return;
    try {
      await backfillMutation.mutateAsync({ id: backfillTargetId, file });
      toast({
        variant: 'success',
        title: 'File re-uploaded',
        description: 'Public links are now available for this file.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Re-upload failed',
        description: extractErrorMessage(error),
      });
    }
    setBackfillTargetId(null);
  };

  const handleSubscribe = async (ragFileId: string, name: string) => {
    try {
      await subscribeMutation.mutateAsync(ragFileId);
      toast({
        variant: 'success',
        title: 'Added',
        description: `"${name}" added to this agent's knowledge base.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to add',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleUnsubscribe = async (ragFileId: string, name: string) => {
    try {
      await unsubscribeMutation.mutateAsync(ragFileId);
      toast({
        variant: 'success',
        title: 'Removed',
        description: `"${name}" removed from this agent's knowledge base.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to remove',
        description: extractErrorMessage(error),
      });
    }
  };

  return (
    <section className="bg-card space-y-5 rounded-xl border p-5">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">Knowledge base (RAG)</h3>
        <p className="text-muted-foreground text-sm">
          Ground this agent on your own documents. Files here belong only to this configuration —
          other agents cannot read them unless you mark a file as shared.
        </p>
      </div>

      {!configurationId ? (
        <div className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">
          Save the configuration first, then come back here to upload knowledge files.
        </div>
      ) : (
        <>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="border-input text-primary h-4 w-4 rounded"
              checked={ragEnabled}
              onChange={(e) => onRagEnabledChange(e.target.checked)}
            />
            Use knowledge base for this configuration
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={backfillInputRef}
              type="file"
              className="hidden"
              onChange={handleBackfillSelected}
              accept=".txt,.md,.pdf,.html,.htm,.csv,.json,.docx"
            />
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelected}
              accept=".txt,.md,.pdf,.html,.htm,.csv,.json,.docx"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-1.5 h-4 w-4" />
              )}
              Upload file
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1">
              <LinkIcon className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
              <Input
                type="url"
                placeholder="https://example.com/page-to-index"
                className="pl-9"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddUrl();
                  }
                }}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddUrl}
              disabled={ingestUrlMutation.isPending || !url.trim()}
            >
              {ingestUrlMutation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <LinkIcon className="mr-1.5 h-4 w-4" />
              )}
              Fetch URL
            </Button>
          </div>

          {/* Files list */}
          <div className="space-y-2">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">Loading files…</p>
            ) : files.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No files yet. Upload a document or fetch a URL to build the knowledge base for this
                agent.
              </p>
            ) : (
              files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="truncate text-sm">{file.displayName}</span>
                    {file.state && (
                      <span
                        className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${
                          STATE_STYLES[file.state] ?? 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {file.state}
                      </span>
                    )}
                    {!file.isOwner && (
                      <span className="text-muted-foreground shrink-0 text-xs italic">
                        From another agent
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {file.isOwner ? (
                      <>
                        {/* Agent-to-agent sharing toggle (owner only) */}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1.5 px-2 text-xs"
                          onClick={() => handleToggleShared(file.id, !file.isShared)}
                          disabled={setSharedMutation.isPending}
                          title={
                            file.isShared
                              ? 'Shared with all agents — click to make private'
                              : 'Private to this configuration — click to share with all agents'
                          }
                        >
                          {file.isShared ? (
                            <>
                              <Globe className="h-3.5 w-3.5" /> Shared
                            </>
                          ) : (
                            <>
                              <Lock className="h-3.5 w-3.5" /> Private
                            </>
                          )}
                        </Button>

                        {/* Public link controls (owner only) */}
                        {file.isPublic && file.publicToken ? (
                          <div className="flex items-center gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1.5 px-2 text-xs"
                              onClick={() => handleCopyPublicLink(file.publicToken!)}
                              title="Copy public link"
                            >
                              <Copy className="h-3.5 w-3.5" /> Copy link
                            </Button>
                            {file.viewCount > 0 && (
                              <span
                                className="text-muted-foreground flex items-center gap-0.5 text-xs"
                                title={`${file.viewCount} view${file.viewCount !== 1 ? 's' : ''}`}
                              >
                                <Eye className="h-3 w-3" />
                                {file.viewCount}
                              </span>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRevokePublic(file.id)}
                              disabled={revokePublicMutation.isPending}
                              title="Revoke public link"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : file.hasStoragePath ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1.5 px-2 text-xs"
                            onClick={() => handleMakePublic(file.id)}
                            disabled={makePublicMutation.isPending}
                            title="Create a public link anyone can use to view this document"
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> Public link
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1.5 px-2 text-xs"
                            onClick={() => {
                              setBackfillTargetId(file.id);
                              backfillInputRef.current?.click();
                            }}
                            disabled={backfillMutation.isPending}
                            title="Re-upload this file to enable public links"
                          >
                            {backfillMutation.isPending && backfillTargetId === file.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Upload className="h-3.5 w-3.5" />
                            )}
                            Re-upload
                          </Button>
                        )}

                        {/* Delete (owner only) */}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                          onClick={() => handleDelete(file.id, file.displayName)}
                          disabled={deleteMutation.isPending}
                          title="Delete this file"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      /* Non-owner: just a Remove button to unsubscribe */
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1.5 px-2 text-xs"
                        onClick={() => handleUnsubscribe(file.id, file.displayName)}
                        disabled={unsubscribeMutation.isPending}
                        title="Remove this shared file from this agent"
                      >
                        <Minus className="h-3.5 w-3.5" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Available shared files from other agents */}
          {availableShared.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-muted-foreground text-sm font-medium">
                Available shared files from other agents
              </h4>
              {availableShared.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Globe className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="truncate text-sm">{file.displayName}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 px-2 text-xs"
                    onClick={() => handleSubscribe(file.id, file.displayName)}
                    disabled={subscribeMutation.isPending}
                    title="Add this shared file to this agent's knowledge base"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};
